import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Card, Form, Input, Button, Switch, message, Space, Spin, Divider, Upload, Image, Empty, Tabs } from 'antd';
import type { UploadProps } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import RichTextEditor from '../components/RichTextEditor';
import { normalizeFileUrl, uploadFile } from '../services/fileService';
import {
  getMiniProgramStaffConfig,
  saveMiniProgramStaffConfig,
  MiniProgramStaffConfig as MiniProgramStaffConfigType,
} from '../services/miniProgramStaffConfigService';

const { Title, Paragraph } = Typography;

type StaffConfigTabKey = 'login' | 'support' | 'policy';

const staffConfigTabLabels: Record<StaffConfigTabKey, string> = {
  login: '登录配置',
  support: '客服配置',
  policy: '政策配置',
};

const staffConfigTabFieldMap: Record<StaffConfigTabKey, (keyof MiniProgramStaffConfigType)[]> = {
  login: ['loginLogo', 'loginLogoFileId'],
  support: ['supportPhone', 'supportWechat', 'supportHelpText', 'supportFaqs', 'enabled'],
  policy: [
    'loginAgreement',
    'privacyPolicy',
    'orderTakingAgreement',
    'orderTakingAgreementFileId',
    'securityDepositAgreement',
    'securityDepositAgreementFileId',
  ],
};

const createDefaultValues = (): MiniProgramStaffConfigType => ({
  loginLogo: '',
  loginLogoFileId: '',
  supportPhone: '',
  supportWechat: '',
  supportHelpText: '',
  supportFaqs: [],
  loginAgreement: '',
  privacyPolicy: '',
  orderTakingAgreement: '',
  orderTakingAgreementFileId: '',
  securityDepositAgreement: '',
  securityDepositAgreementFileId: '',
  enabled: true,
});

const MiniProgramStaffConfig: React.FC = () => {
  const [form] = Form.useForm<MiniProgramStaffConfigType>();
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [agreementUploading, setAgreementUploading] = useState(false);
  const [depositAgreementUploading, setDepositAgreementUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<StaffConfigTabKey>('login');

  const loadConfig = useCallback(async () => {
    setInitialLoading(true);
    try {
      const res = await getMiniProgramStaffConfig();
      const data = res.data || createDefaultValues();
      form.setFieldsValue({
        ...createDefaultValues(),
        ...data,
        supportFaqs: Array.isArray(data.supportFaqs) ? data.supportFaqs : [],
      });
    } catch {
      message.error('加载配置失败');
      form.setFieldsValue(createDefaultValues());
    } finally {
      setInitialLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loginLogo = Form.useWatch('loginLogo', form);
  const orderTakingAgreement = Form.useWatch('orderTakingAgreement', form);
  const orderTakingAgreementFileId = Form.useWatch('orderTakingAgreementFileId', form);
  const securityDepositAgreement = Form.useWatch('securityDepositAgreement', form);
  const securityDepositAgreementFileId = Form.useWatch('securityDepositAgreementFileId', form);

  const handleLogoUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setLogoUploading(true);
    try {
      const res = await uploadFile(file as File, 'mini_program_staff_login_logo');
      form.setFieldsValue({
        loginLogo: res.data.url,
        loginLogoFileId: res.data.id,
      });
      message.success('Logo 上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Logo 上传失败');
      onError?.(error);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleAgreementUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const uploadFileObject = file as File;
    const fileName = uploadFileObject.name || '';
    if (!/\.(doc|docx)$/i.test(fileName)) {
      message.error('请上传 doc 或 docx 文件');
      onError?.(new Error('invalid file type'));
      return;
    }

    setAgreementUploading(true);
    try {
      const res = await uploadFile(uploadFileObject, 'mini_program_staff_order_taking_agreement');
      form.setFieldsValue({
        orderTakingAgreement: res.data.url,
        orderTakingAgreementFileId: res.data.id,
      });
      message.success('接单协议上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '接单协议上传失败');
      onError?.(error);
    } finally {
      setAgreementUploading(false);
    }
  };

  const clearAgreementFile = () => {
    form.setFieldsValue({
      orderTakingAgreement: '',
      orderTakingAgreementFileId: '',
    });
  };

  const handleDepositAgreementUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const uploadFileObject = file as File;
    const fileName = uploadFileObject.name || '';
    if (!/\.(doc|docx)$/i.test(fileName)) {
      message.error('请上传 doc 或 docx 文件');
      onError?.(new Error('invalid file type'));
      return;
    }

    setDepositAgreementUploading(true);
    try {
      const res = await uploadFile(uploadFileObject, 'mini_program_staff_security_deposit_agreement');
      form.setFieldsValue({
        securityDepositAgreement: res.data.url,
        securityDepositAgreementFileId: res.data.id,
      });
      message.success('保证金协议上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '保证金协议上传失败');
      onError?.(error);
    } finally {
      setDepositAgreementUploading(false);
    }
  };

  const clearDepositAgreementFile = () => {
    form.setFieldsValue({
      securityDepositAgreement: '',
      securityDepositAgreementFileId: '',
    });
  };

  const saveConfig = async (values: MiniProgramStaffConfigType) => {
    setSubmitting(true);
    try {
      await saveMiniProgramStaffConfig({
        ...values,
        supportFaqs: (values.supportFaqs || []).map((item, index) => ({
          question: item.question,
          answer: item.answer,
          sort: index + 1,
        })),
      });
      message.success('保存成功');
      await loadConfig();
    } catch {
      message.error('保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values: MiniProgramStaffConfigType) => {
    await saveConfig(values);
  };

  const handleSaveCurrentTab = async () => {
    try {
      await form.validateFields(staffConfigTabFieldMap[activeTab]);
      await saveConfig(form.getFieldsValue(true) as MiniProgramStaffConfigType);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error('保存失败');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
          <div>
            <Title level={2}>服务人员小程序配置</Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              配置服务人员端“帮助客服”页面的客服信息、常见问题、登录协议、隐私政策和接单协议。
            </Paragraph>
          </div>
          <Button type="primary" icon={<SaveOutlined />} loading={submitting} onClick={handleSaveCurrentTab}>
            保存{staffConfigTabLabels[activeTab]}
          </Button>
        </div>
      </div>

      <div className="page-content">
        <Spin spinning={initialLoading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={createDefaultValues()}>
            <Tabs activeKey={activeTab} onChange={key => setActiveTab(key as StaffConfigTabKey)} style={{ marginBottom: 24 }}>
              <Tabs.TabPane tab="登录配置" key="login">
                <Card title="登录配置">
              <Form.Item name="loginLogo" label="登录 Logo">
                <Input placeholder="请上传登录 Logo" readOnly />
              </Form.Item>

              <Form.Item name="loginLogoFileId" hidden>
                <Input />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  <Upload accept="image/*" showUploadList={false} customRequest={handleLogoUpload}>
                    <Button loading={logoUploading} icon={<UploadOutlined />}>
                      上传 Logo
                    </Button>
                  </Upload>
                  {loginLogo ? (
                    <Image
                      src={normalizeFileUrl(loginLogo)}
                      alt="登录 Logo"
                      width={120}
                      style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
                    />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无 Logo" />
                  )}
                </Space>
              </Form.Item>
                </Card>
              </Tabs.TabPane>

              <Tabs.TabPane tab="客服配置" key="support">
                <Card title="客服配置">
              <Form.Item name="supportPhone" label="客服电话">
                <Input placeholder="请输入客服电话" />
              </Form.Item>

              <Form.Item name="supportWechat" label="客服微信">
                <Input placeholder="请输入客服微信号" />
              </Form.Item>

              <Form.Item name="supportHelpText" label="需要帮助文案">
                <Input.TextArea rows={4} placeholder="请输入帮助客服页面顶部说明文案" />
              </Form.Item>

              <Form.Item name="enabled" label="启用状态" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Divider style={{ marginTop: 0 }}>常见问题</Divider>

              <Form.List name="supportFaqs">
                {(fields, { add, remove }) => (
                  <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`常见问题 ${index + 1}`}
                        extra={
                          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                            删除
                          </Button>
                        }
                      >
                        <Form.Item
                          {...field}
                          name={[field.name, 'question']}
                          label="问题"
                          rules={[{ required: true, message: '请输入问题' }]}
                        >
                          <Input placeholder="请输入常见问题标题" />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'answer']}
                          label="答案"
                          rules={[{ required: true, message: '请输入答案' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input.TextArea rows={4} placeholder="请输入常见问题答案" />
                        </Form.Item>
                      </Card>
                    ))}

                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ question: '', answer: '' })}>
                      新增常见问题
                    </Button>
                  </Space>
                )}
              </Form.List>
                </Card>
              </Tabs.TabPane>

              <Tabs.TabPane tab="政策配置" key="policy">
                <Card title="政策配置">
              <Form.Item name="loginAgreement" label="登录协议">
                <RichTextEditor placeholder="请输入服务人员小程序登录协议内容" />
              </Form.Item>

              <Form.Item name="privacyPolicy" label="隐私政策">
                <RichTextEditor placeholder="请输入服务人员小程序隐私政策内容" />
              </Form.Item>

              <Form.Item name="orderTakingAgreement" label="接单协议文件">
                <Input placeholder="请上传接单协议 doc/docx 文件" readOnly />
              </Form.Item>

              <Form.Item name="orderTakingAgreementFileId" hidden>
                <Input />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space wrap>
                  <Upload
                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    showUploadList={false}
                    customRequest={handleAgreementUpload}
                  >
                    <Button loading={agreementUploading} icon={<UploadOutlined />}>
                      上传接单协议
                    </Button>
                  </Upload>
                  {orderTakingAgreement ? (
                    <>
                      <Button href={normalizeFileUrl(orderTakingAgreement)} target="_blank">
                        查看/下载
                      </Button>
                      <Button danger icon={<DeleteOutlined />} onClick={clearAgreementFile}>
                        清除
                      </Button>
                      <span style={{ color: '#8c8c8c' }}>文件ID：{orderTakingAgreementFileId || '-'}</span>
                    </>
                  ) : null}
                </Space>
              </Form.Item>

              <Form.Item name="securityDepositAgreement" label="保证金协议文件" style={{ marginTop: 24 }}>
                <Input placeholder="请上传保证金协议 doc/docx 文件" readOnly />
              </Form.Item>

              <Form.Item name="securityDepositAgreementFileId" hidden>
                <Input />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space wrap>
                  <Upload
                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    showUploadList={false}
                    customRequest={handleDepositAgreementUpload}
                  >
                    <Button loading={depositAgreementUploading} icon={<UploadOutlined />}>
                      上传保证金协议
                    </Button>
                  </Upload>
                  {securityDepositAgreement ? (
                    <>
                      <Button href={normalizeFileUrl(securityDepositAgreement)} target="_blank">
                        查看/下载
                      </Button>
                      <Button danger icon={<DeleteOutlined />} onClick={clearDepositAgreementFile}>
                        清除
                      </Button>
                      <span style={{ color: '#8c8c8c' }}>文件ID：{securityDepositAgreementFileId || '-'}</span>
                    </>
                  ) : null}
                </Space>
              </Form.Item>
                </Card>
              </Tabs.TabPane>
            </Tabs>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default MiniProgramStaffConfig;
